import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { GlobalSearchResponse } from '@orion/contracts';
import { useGlobalSearch } from './api.js';
import { closeCommandPalette, openCommandPalette, toggleCommandPalette, useCommandPaletteOpen } from './palette-store.js';

type EntityType = GlobalSearchResponse['groups'][number]['entityType'];
type SearchResult = GlobalSearchResponse['groups'][number]['results'][number];

const ENTITY_ROUTE: Partial<Record<EntityType, string>> = {
  lead: '/leads/$leadId',
  contact: '/contacts/$contactId',
  company: '/companies/$companyId',
  opportunity: '/deals/$dealId',
};
const ENTITY_PARAM: Partial<Record<EntityType, string>> = {
  lead: 'leadId',
  contact: 'contactId',
  company: 'companyId',
  opportunity: 'dealId',
};
const ENTITY_LABEL: Record<EntityType, string> = { lead: 'Leads', contact: 'Contacts', company: 'Companies', opportunity: 'Opportunities', task: 'Tasks', note: 'Notes' };
const SCOPE_ORDER: EntityType[] = ['lead', 'contact', 'company', 'opportunity', 'task', 'note'];

const RECENT_KEY = 'orion-search-recent';
const MAX_RECENT = 5;

interface RecentItem {
  entityType: string;
  id: string;
  title: string;
  subtitle?: string | null;
}

function loadRecent(): RecentItem[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? (JSON.parse(raw) as RecentItem[]) : [];
  } catch {
    return [];
  }
}

function pushRecent(item: RecentItem) {
  try {
    const next = [item, ...loadRecent().filter((r) => !(r.entityType === item.entityType && r.id === item.id))].slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Recent-items convenience only — fine if a private window drops it.
  }
}

function Highlight({ text, q }: { text: string; q: string }): ReactNode {
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <strong className="font-bold text-ink">{text.slice(idx, idx + q.length)}</strong>
      {text.slice(idx + q.length)}
    </>
  );
}

/** docs section 21 "Search" — Ctrl K anywhere in the app, or clicking the
 * Topbar's search trigger (`palette-store.ts` shares the open state
 * between both entry points). Permission-aware on the server; scope tabs
 * and the "N records" count are computed client-side from the same
 * response, not a second request. Match highlighting is a plain substring
 * match against the query, same idea as the design's `<mark>` — not a
 * fuzzy-match/ranking engine. No "Create <type> named '…'" or "Go to X"
 * action rows this pass — the design shows them, but wiring a real create-
 * from-search flow (vs a dead button) is its own scope. */
// design/orion-crm-design/rendered-html/Search.html captures the palette
// open with a query already typed, over the Leads list — `?__design=Search`
// (dev-only) reproduces both the same way LeadDetailPage does for
// ConvertLeadModal.
const isDesignSearchPreview = import.meta.env.DEV && new URLSearchParams(window.location.search).get('__design') === 'Search';

export function CommandPalette() {
  const open = useCommandPaletteOpen();
  const [q, setQ] = useState(() => (isDesignSearchPreview ? 'north' : ''));
  const [scope, setScope] = useState<EntityType | 'all'>('all');
  const [highlighted, setHighlighted] = useState(0);
  const navigate = useNavigate();
  const { data, isFetching } = useGlobalSearch(q);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        toggleCommandPalette();
      }
      if (e.key === 'Escape') closeCommandPalette();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open && !isDesignSearchPreview) {
      setQ('');
      setScope('all');
      setHighlighted(0);
    }
  }, [open]);

  useEffect(() => {
    if (isDesignSearchPreview) openCommandPalette();
  }, []);

  const groups = useMemo(() => (scope === 'all' ? (data?.groups ?? []) : (data?.groups ?? []).filter((g) => g.entityType === scope)), [data, scope]);
  const totalRecords = useMemo(() => (data?.groups ?? []).reduce((sum, g) => sum + g.total, 0), [data]);

  const flatResults = useMemo(() => {
    if (!q) return loadRecent().map((r) => ({ ...r, group: 'Recent' }));
    return groups.flatMap((g) => g.results.map((r) => ({ entityType: g.entityType, ...r, group: ENTITY_LABEL[g.entityType] })));
  }, [q, groups]);

  useEffect(() => {
    setHighlighted(0);
  }, [flatResults.length]);

  if (!open) return null;

  const go = (entityType: EntityType | string, result: { id: string; title: string; subtitle?: string | null; linkTo?: SearchResult['linkTo'] }) => {
    const target = result.linkTo ? { entityType: result.linkTo.entityType, id: result.linkTo.id } : { entityType, id: result.id };
    const route = ENTITY_ROUTE[target.entityType as EntityType];
    const param = ENTITY_PARAM[target.entityType as EntityType];
    if (route && param) {
      if (!result.linkTo) pushRecent({ entityType, id: result.id, title: result.title, subtitle: result.subtitle });
      navigate({ to: route, params: { [param]: target.id } } as never);
      closeCommandPalette();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      const item = flatResults[highlighted];
      if (item) go(item.entityType, item);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      const order: (EntityType | 'all')[] = ['all', ...SCOPE_ORDER];
      const next = order[(order.indexOf(scope) + 1) % order.length]!;
      setScope(next);
    }
  };

  let flatIndex = -1;

  return (
    <div className="fixed inset-0 z-50 flex justify-center bg-night/40 pt-28" onClick={() => closeCommandPalette()}>
      <div
        role="dialog"
        aria-label="Search Orion"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
        style={{ width: '680px' }}
        className="flex h-fit max-h-[70vh] flex-col overflow-hidden rounded-dialog border border-border bg-surface shadow-dialog"
      >
        <label className="flex h-14 shrink-0 items-center gap-3 border-b border-divider px-5">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <span className="sr-only">Search everything</span>
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            type="search"
            aria-controls="palette-results"
            placeholder="Search everything"
            className="flex-1 text-[17px] text-ink outline-none placeholder:text-text-muted"
          />
          {q && !isFetching && <span className="text-xs tabular-nums text-text-muted">{totalRecords} record{totalRecords === 1 ? '' : 's'}</span>}
          <kbd className="rounded-sm border border-border bg-ground px-1.5 py-0.5 font-mono text-[11px] text-text-muted">Esc</kbd>
        </label>

        {q && (
          <div role="tablist" aria-label="Search scope" className="flex h-12 shrink-0 items-center gap-1.5 border-b border-divider px-4">
            <button
              type="button"
              role="tab"
              aria-selected={scope === 'all'}
              onClick={() => setScope('all')}
              className={`flex h-7 items-center rounded-pill px-2.5 text-xs font-semibold ${scope === 'all' ? 'bg-night text-white' : 'border border-border text-text-secondary'}`}
            >
              All
            </button>
            {SCOPE_ORDER.map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={scope === t}
                onClick={() => setScope(t)}
                className={`flex h-7 items-center gap-1 rounded-pill px-2.5 text-xs font-semibold ${scope === t ? 'bg-night text-white' : 'border border-border text-text-secondary'}`}
              >
                {ENTITY_LABEL[t]} <span className="font-medium text-text-muted">{data?.groups.find((g) => g.entityType === t)?.total ?? 0}</span>
              </button>
            ))}
          </div>
        )}

        <div id="palette-results" role="listbox" aria-label="Results" className="flex-1 overflow-y-auto p-2">
          {!q && flatResults.length === 0 && <p className="px-2 py-3 text-sm text-text-muted">Start typing to search everything.</p>}
          {isFetching && <p className="px-2 py-3 text-sm text-text-muted">Searching…</p>}
          {!isFetching && q && groups.length === 0 && <p className="px-2 py-3 text-sm text-text-muted">No results.</p>}

          {!q && flatResults.length > 0 && <p className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">Recent</p>}
          {!q &&
            flatResults.map((r) => {
              flatIndex += 1;
              const idx = flatIndex;
              return (
                <button
                  key={`${r.entityType}-${r.id}`}
                  type="button"
                  role="option"
                  aria-selected={highlighted === idx}
                  onMouseEnter={() => setHighlighted(idx)}
                  onClick={() => go(r.entityType, r)}
                  className={`flex w-full flex-col items-start rounded-input px-3 py-2 text-left text-sm ${highlighted === idx ? 'bg-primary-soft' : ''}`}
                >
                  <span className="font-medium text-ink">{r.title}</span>
                  {r.subtitle && <span className="text-xs text-text-muted">{r.subtitle}</span>}
                </button>
              );
            })}

          {q &&
            groups.map((g) => (
              <div key={g.entityType} className="mb-1">
                <p className="flex h-7 items-center justify-between px-3 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                  {ENTITY_LABEL[g.entityType]} <span className="tabular-nums normal-case tracking-normal">{g.total}</span>
                </p>
                {g.results.map((r) => {
                  flatIndex += 1;
                  const idx = flatIndex;
                  const selected = highlighted === idx;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onMouseEnter={() => setHighlighted(idx)}
                      onClick={() => go(g.entityType, r)}
                      className={`flex h-11 w-full items-center gap-3 rounded-input px-3 text-left ${selected ? 'bg-primary-soft' : ''}`}
                    >
                      <span className="flex size-7 shrink-0 items-center justify-center rounded-input bg-surface-subtle text-[11px] font-[650] text-text-secondary">{ENTITY_LABEL[g.entityType][0]}</span>
                      <span className="min-w-0 flex-1 truncate text-sm text-ink">
                        <Highlight text={r.title} q={q} />
                        {r.subtitle && <span className="text-text-muted"> · {r.subtitle}</span>}
                      </span>
                      {r.badge && (
                        <span className="flex h-5.5 shrink-0 items-center gap-1.5 rounded-pill px-2 text-xs font-semibold" style={{ background: `${r.badge.color}1A`, color: r.badge.color }}>
                          {r.badge.label}
                        </span>
                      )}
                      {selected && <kbd className="shrink-0 rounded-xs border border-border-strong bg-surface px-1.5 py-0.5 font-mono text-[11px] text-primary-soft-text">↵</kbd>}
                    </button>
                  );
                })}
              </div>
            ))}
        </div>

        <div className="flex h-11 shrink-0 items-center gap-4 border-t border-divider bg-surface-subtle px-5 text-xs text-text-muted">
          <span className="flex items-center gap-1.5">
            <kbd className="rounded-xs border border-border bg-surface px-1 py-0.5 font-mono text-[11px]">↑↓</kbd>navigate
          </span>
          <span className="flex items-center gap-1.5">
            <kbd className="rounded-xs border border-border bg-surface px-1 py-0.5 font-mono text-[11px]">↵</kbd>open
          </span>
          <span className="flex items-center gap-1.5">
            <kbd className="rounded-xs border border-border bg-surface px-1 py-0.5 font-mono text-[11px]">Tab</kbd>filter
          </span>
          <span className="flex items-center gap-1.5">
            <kbd className="rounded-xs border border-border bg-surface px-1 py-0.5 font-mono text-[11px]">Esc</kbd>close
          </span>
        </div>
      </div>
    </div>
  );
}
